import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { deleteLocalSession } from '@/session/db.js';
import { currentImageRoll, currentPitch, medianTilt } from '@/lib/motion.js';
import { useTilt } from '@/features/capture/useTilt.js';
import { CameraLoading } from '@/features/capture/CameraLoading.js';
import { Navigate, useNavigate } from 'react-router';
import { DISCLAIMERS } from '@erectus/shared';
import { Button } from '@/ui/primitives.js';
import { STATE_LABEL, STATE_STYLE } from '@/scoring/frameState.js';
import { useAuth } from '@/stores/auth.js';
import { useSession } from '@/stores/session.js';
import { useCapture } from '@/features/capture/useCapture.js';
import { SkeletonOverlay } from '@/features/capture/SkeletonOverlay.js';
import { shouldShowGateBanner, shouldShowThermalHint } from '@/session/recorder.js';
import { finalizeSession } from '@/session/finalize.js';
import { uploadKeypoints } from '@/session/upload.js';
import { minuteBucket, track, validRatioBucket } from '@/lib/analytics.js';
import { deviceClass } from '@/lib/device.js';
import { playCue, setCuesEnabled } from '@/lib/cues.js';
import { SLOW_CAPTURE } from '@erectus/shared';
import { reportError } from '@/lib/crashlytics.js';
import { Icon } from '@/ui/icons.js';

/**
 * Live session (docs/PRODUCT.md §2 screen 5).
 *
 * Full-screen portrait preview with the status laid over it — the phone is two
 * metres away, so the state indicator has to be legible from the desk, and the
 * controls only matter when you walk back.
 *
 * The camera continues on whichever lens the shot was framed with: switching
 * to the rear camera after someone set up on the front would point the phone at
 * the wrong half of the room for the whole hour.
 */
export function LiveSessionPage(): ReactNode {
  const navigate = useNavigate();
  const { user, userDoc } = useAuth();
  const { sessionId, plannedMin, sampleFps, poseModel, calibration, facing, cues, clear } =
    useSession();
  const [dim, setDim] = useState(userDoc?.settings?.dimByDefault ?? false);
  const [ending, setEnding] = useState(false);
  const [thermalHints, setThermalHints] = useState(0);
  const startedAt = useRef(Date.now());
  const started = useRef(false);

  const capture = useCapture({
    sessionId: sessionId ?? 'pending',
    ...(user ? { uid: user.uid } : {}),
    sampleFps,
    poseModel,
    calibration,
    facing,
  });

  useEffect(() => {
    setCuesEnabled(cues);
  }, [cues]);

  useEffect(() => {
    if (!capture.ready || started.current) return;
    started.current = true;
    startedAt.current = Date.now();
    void capture.start();
    track('session_start', {
      planned_min_bucket: minuteBucket(plannedMin),
      device_class: deviceClass(),
      pose_model: poseModel,
      fps: (sampleFps === 2 || sampleFps === 4 || sampleFps === 5 ? sampleFps : 3) as 2 | 3 | 4 | 5,
    });
  }, [capture, plannedMin, poseModel, sampleFps]);

  const elapsedMs = capture.tick?.elapsedMs ?? 0;
  useEffect(() => {
    if (capture.phase === 'paused' && capture.pauseReason) {
      track('session_pause', { reason: capture.pauseReason });
      // No sound for a pause: automatic ones are recognition failures, which
      // stay silent (CLAUDE.md capture invariant 4); the screen says why.
    }
  }, [capture.phase, capture.pauseReason]);

  useEffect(() => {
    if (shouldShowThermalHint(elapsedMs, thermalHints)) setThermalHints((n) => n + 1);
  }, [elapsedMs, thermalHints]);

  const [finishing, setFinishing] = useState(false);

  // Phone tilt every 5 s while recording; the medians go on the summary so the
  // levelling (roll) can be audited and the lean (pitch) accounted for.
  useTilt();
  const rolls = useRef<number[]>([]);
  const pitches = useRef<number[]>([]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (capture.phase !== 'running') return;
      const roll = currentImageRoll(capture.facing);
      const pitch = currentPitch();
      if (roll !== null) rolls.current.push(roll);
      if (pitch !== null) pitches.current.push(pitch);
    }, 5000);
    return () => window.clearInterval(timer);
  }, [capture.phase, capture.facing]);

  /** Throw the session away: no summary, no report, no local frames. */
  const discard = useCallback(async () => {
    if (!sessionId || ending) return;
    setEnding(true);
    await capture.stop();
    await deleteLocalSession(sessionId).catch(() => undefined);
    track('session_discarded', { elapsed_min_bucket: minuteBucket(elapsedMs / 60_000) });
    clear();
    navigate('/progress', { replace: true });
  }, [capture, clear, elapsedMs, ending, navigate, sessionId]);

  const end = useCallback(
    async (endedBy: 'user' | 'error' | 'background') => {
      if (!user || !sessionId || ending) return;
      setEnding(true);
      playCue('finish');
      await capture.stop();
      const result = await finalizeSession({
        sessionId,
        uid: user.uid,
        startedAt: startedAt.current,
        endedAt: Date.now(),
        plannedMin,
        sampleFps,
        poseModel,
        framingScore: capture.tick?.framing ?? 0,
        dataOptIn: userDoc?.consent?.dataOptIn ?? false,
        disagreementCount: capture.disagreementCount(),
        phoneRollDeg: medianTilt(rolls.current),
        phonePitchDeg: medianTilt(pitches.current),
      });
      track('session_end', {
        actual_min_bucket: minuteBucket(result.summary.durationSec / 60),
        valid_ratio_bucket: validRatioBucket(result.summary.validRatio),
        ended_by: endedBy,
      });
      void uploadKeypoints({
        uid: user.uid,
        sessionId,
        sampleFps,
        dataOptIn: userDoc?.consent?.dataOptIn ?? false,
      });
      clear();
      navigate(`/report/${sessionId}`, { replace: true });
    },
    [
      capture,
      clear,
      ending,
      navigate,
      plannedMin,
      poseModel,
      sampleFps,
      sessionId,
      user,
      userDoc?.consent?.dataOptIn,
    ],
  );

  useEffect(() => {
    if (elapsedMs >= plannedMin * 60 * 1000 && !ending) void end('user');
  }, [elapsedMs, end, ending, plannedMin]);

  useEffect(() => {
    if (capture.error) track('session_error', { code: capture.error.code as 'model_load' });
  }, [capture.error]);

  // Is the phone keeping the fixed sample rate? Below SLOW_CAPTURE for a full
  // minute of recording: report once (Crashlytics with the device, GA4 as a
  // count). The rate is never lowered; the summary's actualFps records it.
  const slowSince = useRef<number | null>(null);
  const slowReported = useRef(false);
  const measuredFps = capture.tick?.fps ?? 0;
  useEffect(() => {
    if (capture.phase !== 'running' || slowReported.current || measuredFps === 0) {
      if (capture.phase !== 'running') slowSince.current = null;
      return;
    }
    if (measuredFps >= sampleFps * SLOW_CAPTURE.belowFraction) {
      slowSince.current = null;
      return;
    }
    const now = Date.now();
    slowSince.current ??= now;
    if (now - slowSince.current < SLOW_CAPTURE.forSec * 1000) return;
    slowReported.current = true;
    const bucket = measuredFps < 1 ? '<1' : measuredFps < 2 ? '1-2' : measuredFps < 3 ? '2-3' : '3+';
    track('capture_slow', { fps_bucket: bucket, pose_model: poseModel });
    void reportError('capture-slow', new Error(`sampling ${measuredFps} fps, target ${sampleFps}`), {
      fps: String(measuredFps),
      target: String(sampleFps),
      poseModel,
    });
  }, [capture.phase, measuredFps, poseModel, sampleFps]);

  if (!sessionId) return <Navigate to="/capture" replace />;

  const tick = capture.tick;
  const state = tick?.state ?? 'invalid';
  const style = STATE_STYLE[state];
  const progress = Math.min(100, (elapsedMs / (plannedMin * 60 * 1000)) * 100);
  const bannerVisible = shouldShowGateBanner(tick?.gateFailingSec ?? 0);

  return (
    <div className="fixed inset-0 overflow-hidden bg-ink">
      {/* Front camera mirrored, picture and skeleton together (as in setup). */}
      <div
        className="absolute inset-0"
        style={{ transform: capture.facing === 'user' ? 'scaleX(-1)' : undefined }}
      >
        <video
          ref={capture.videoRef}
          playsInline
          muted
          className={`absolute inset-0 size-full object-cover transition-opacity duration-200 ${capture.previewReady ? 'opacity-100' : 'opacity-0'}`}
        />
        <SkeletonOverlay
          landmarks={tick?.landmarks ?? []}
          videoRef={capture.videoRef}
          side={tick?.gate.side}
          color={style.color}
          fit="cover"
        />
      </div>

      {/* Tap anywhere to wake from dim mode. */}
      {dim ? (
        <button
          className="absolute inset-0 z-30 bg-ink/95"
          onClick={() => setDim(false)}
          aria-label="Wake the screen"
        >
          <span className="absolute inset-x-0 bottom-24 text-center text-xs text-white/40">
            Recording · tap to show
          </span>
        </button>
      ) : null}

      <div className="absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-ink/80 to-transparent px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pb-10 text-white">
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-3xl font-bold tabular-nums">{formatClock(elapsedMs)}</p>
          <p className="text-sm opacity-75">of {plannedMin} min</p>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/25">
          <div className="h-full rounded-full bg-white" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* The one thing that must read from across the room. */}
      <div className="pointer-events-none absolute inset-x-0 top-28 z-10 flex justify-center px-4">
        <div
          role="status"
          aria-live="polite"
          className="flex items-center gap-3 rounded-2xl px-5 py-3 text-xl font-bold shadow-xl"
          style={{ backgroundColor: style.soft, color: style.color }}
        >
          <Icon name={style.icon} size={32} strokeWidth={2.2} />
          {STATE_LABEL[state]}
        </div>
      </div>

      {capture.phase === 'paused' ? (
        <div className="absolute inset-x-4 top-52 z-20 rounded-2xl bg-white p-4 text-center shadow-lg">
          <p className="font-semibold">Paused</p>
          <p className="mt-1 text-sm text-muted">{pauseText(capture.pauseReason)}</p>
          {capture.pauseReason === 'user' ? (
            <Button className="mt-3" onClick={capture.resume}>
              Resume
            </Button>
          ) : null}
        </div>
      ) : null}

      {bannerVisible ? (
        <div className="absolute inset-x-4 bottom-44 z-20 rounded-2xl bg-[color:var(--color-state-fhp-soft)] p-3 text-sm shadow-lg">
          <p className="font-medium">
            Nothing measurable for {Math.round(tick?.gateFailingSec ?? 0)} seconds.
          </p>
          <p className="mt-1 text-xs">
            {tick?.gate.reasons.join(', ') || 'Check the camera angle and lighting.'}
          </p>
        </div>
      ) : null}

      {capture.error ? (
        <div className="absolute inset-x-4 top-52 z-20 rounded-2xl bg-white p-4 text-sm shadow-lg">
          <p className="font-semibold">Session interrupted</p>
          <p className="mt-1 text-ink-soft">{capture.error.message}</p>
          <Button variant="secondary" className="mt-3" onClick={() => void end('error')}>
            Save what we have
          </Button>
        </div>
      ) : null}

      <div className="absolute inset-x-0 bottom-0 z-10 space-y-3 bg-gradient-to-t from-ink/85 to-transparent px-4 pt-12 pb-[calc(1rem+env(safe-area-inset-bottom))]">
        {thermalHints > 0 ? (
          <p className="text-center text-xs text-white/70">
            Long session — plug the phone in and use dim mode to keep it cool.
          </p>
        ) : null}
        <div className="grid grid-cols-3 gap-2 text-center">
          <Metric label="Neck" value={tick?.metrics.cva} unit="°" />
          <Metric label="Trunk" value={tick?.metrics.trunkInclination} unit="°" />
          <Metric label="Shoulder" value={tick?.metrics.shoulderProtraction} unit="" digits={2} />
        </div>
        <p className="text-center text-[11px] text-white/60">{DISCLAIMERS.accuracy}</p>
        <div className="flex gap-2">
          <Button variant="secondary" className="flex-1" onClick={() => setDim(true)}>
            Dim
          </Button>
          {capture.phase === 'running' ? (
            <Button variant="secondary" className="flex-1" onClick={() => capture.pause()}>
              Pause
            </Button>
          ) : null}
          <Button
            variant="danger"
            className="flex-1 bg-white"
            disabled={ending}
            onClick={() => setFinishing(true)}
          >
            {ending ? 'Saving…' : 'Finish'}
          </Button>
        </div>
        {finishing && !ending ? (
          <div className="space-y-2 rounded-2xl bg-white p-3 text-ink">
            <p className="text-sm font-semibold">End this session?</p>
            <p className="text-xs text-ink-soft">
              Save it for your report, or discard it if the measurement went wrong — nothing from
              it is kept.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <Button variant="secondary" onClick={() => setFinishing(false)}>
                Keep going
              </Button>
              <Button variant="danger" onClick={() => void discard()}>
                Discard
              </Button>
              <Button onClick={() => void end('user')}>Save</Button>
            </div>
          </div>
        ) : null}
      </div>
      <CameraLoading ready={capture.previewReady || Boolean(capture.error)} />
    </div>
  );
}

function Metric({
  label,
  value,
  unit,
  digits = 1,
}: {
  label: string;
  value: number | null | undefined;
  unit: string;
  digits?: number;
}): ReactNode {
  return (
    <div className="rounded-xl bg-white/15 px-2 py-2 backdrop-blur">
      <p className="text-[11px] text-white/70">{label}</p>
      <p className="font-mono text-base font-semibold tabular-nums text-white">
        {value === null || value === undefined ? '—' : `${value.toFixed(digits)}${unit}`}
      </p>
    </div>
  );
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

function pauseText(reason: string | null): string {
  switch (reason) {
    case 'background':
      return 'The camera cannot run while the app is in the background. Come back to resume.';
    case 'multi_person':
      return 'Someone else is in frame. Recording resumes automatically once they leave.';
    case 'gate_failed':
      return 'The view is not measurable right now.';
    case 'break':
      return 'On a rest break.';
    default:
      return 'Recording is paused.';
  }
}
